/*============================================================================
  qty-limit.js - per-order quantity limit clamp (WS: metafield qty limit)

  Data source: product metafield custom.max_per_order (number_integer).
    - PDP  : snippets/product-template.liquid passes `max` to
             snippets/quantity-input.liquid, which emits data-qty-max.
    - Cart : sections/main-cart.liquid sets data-qty-max on each line's
             [data-cart-item-quantity] input from the baked rsc-inventory-data.

  Contract: this file NEVER edits assets/theme.js. The vendor theme.QtySelector
  clamps to `min` only and has no max support, so the clamp is layered on with
  DELEGATED listeners in the CAPTURE phase - they run BEFORE the vendor's
  element-level handlers, so the vendor reads an already-clamped value and
  dispatches the already-clamped quantity to the cart.

  No-op guarantee: with no [data-qty-max] anywhere on the page (i.e. every
  product's metafield blank) all three listeners hit an early return on their
  first line and nothing else in this file ever executes.

  Reversal: remove the qty-limit.js tag in layout/theme.liquid, delete this file.
==============================================================================*/
(function () {
  'use strict';

  // Idempotency gate - safe against double-inclusion.
  if (window.__rsQtyLimit) return;
  window.__rsQtyLimit = true;

  var SEL_MAX_INPUT = '[data-qty-max]';
  var SEL_PLUS      = '.js-qty__adjust--plus';
  var SEL_WRAPPER   = '.js-qty__wrapper';

  // Read the limit off an input. Returns null when there is no usable limit,
  // which is the blank-metafield case and makes every caller a no-op.
  function readMax(input) {
    if (!input || !input.getAttribute) return null;
    var max = parseInt(input.getAttribute('data-qty-max'), 10);
    if (isNaN(max) || max <= 0) return null;
    return max;
  }

  // PDP inputs carry min="1", cart inputs carry min="0". Fall back to 1.
  function readMin(input) {
    var min = parseInt(input.getAttribute('min'), 10);
    return isNaN(min) ? 1 : min;
  }

  // Clamp the field's own value. Returns true if the value was rewritten.
  // Never dispatches events - the vendor handler owns cart syncing.
  function clampInput(input, enforceMin) {
    var max = readMax(input);
    if (max === null) return false;

    var raw = String(input.value).trim();
    // Empty field mid-edit: leave it alone so the shopper can clear and retype.
    if (raw === '') return false;

    var val = parseInt(raw, 10);
    if (isNaN(val)) return false;

    var next = val;
    if (next > max) next = max;
    if (enforceMin) {
      var min = readMin(input);
      if (next < min) next = min;
    }

    if (next !== val) {
      input.value = String(next);
      return true;
    }
    return false;
  }

  // --- Typing / pasting -----------------------------------------------------
  // Capture phase: clamp before anything downstream reads the value.
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches(SEL_MAX_INPUT)) return;
    clampInput(t, false);
  }, true);

  // --- Commit (blur / enter) ------------------------------------------------
  // Capture phase is load-bearing here: the vendor's own `change` listener is
  // bound on the input element itself, so it runs AFTER this one and therefore
  // reads (and on the cart page, POSTs) the clamped value.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches(SEL_MAX_INPUT)) return;
    clampInput(t, true);
  }, true);

  // --- Plus button ----------------------------------------------------------
  // At the limit we stop propagation in the capture phase so the event never
  // reaches the vendor's click handler - the increment simply does not happen
  // and no cart:quantity event is dispatched. preventDefault is deliberately
  // NOT called (these are type="button" controls; no form submission involved).
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var plus = t.closest(SEL_PLUS);
    if (!plus) return;

    var wrapper = plus.closest(SEL_WRAPPER);
    if (!wrapper) return;

    var input = wrapper.querySelector(SEL_MAX_INPUT);
    if (!input) return;

    var max = readMax(input);
    if (max === null) return;

    var val = parseInt(String(input.value).trim(), 10);
    if (isNaN(val)) return;

    if (val >= max) {
      // Already at (or above) the limit. Block the increase.
      // NOTE: we deliberately do NOT rewrite input.value here. On the cart page
      // an over-limit value is real server state (URL-forced) - silently
      // displaying a lower number would desync the DOM from the cart and let
      // the checkout gate read a compliant quantity that isn't actually in the
      // cart. Leaving it visible keeps the gate closed until the shopper
      // actually reduces it.
      e.stopPropagation();
      return;
    }

    // Under the limit: let the vendor increment run, then re-assert the ceiling
    // on the next tick as a belt-and-braces check. Restricted to non-cart
    // inputs so this can never desync a cart line from its server quantity.
    if (!input.hasAttribute('data-cart-item-quantity')) {
      setTimeout(function () { clampInput(input, false); }, 0);
    }
  }, true);
})();
